import {
  Box,
  Button,
  CircularProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { setPageName } from '../../store/redux/reducers/pageNameSlice'
import { useAppDispatch } from '../../store/redux/hooks'
import Searchbar from '../../components/Searchbar/Searchbar'
import { RequestProps } from '../../store/types'
import SortButton from '../../components/Button/SortButton'
import { Add, ViewCompact, ViewModule } from '@mui/icons-material'
import { useGetTechnologiesQuery, useGetTechnologyTypesQuery } from '../../store/redux/reducers/technologiesSlice'
import { StyledContainerCentered, StyledContainerColumnLeft } from '../../styles/ContainerStyles'
import Message from '../../components/Message/Message'
import TechnologyTile from '../../components/Tile/TechnologyTile'
import TechTilesContainer from '../../components/Container/TechTilesContainer'
import AddTechnologyPopup from '../../components/Popup/AddTechnologyPopup'
import { pageHeaderActions } from '../../store/redux/reducers/pageHeaderSlice'
import SingleFilterPopup from '../../components/Popup/SingleFilterPopup'
import { formatTechnologyType } from '../../store/utils'
import { SquareButton } from '../../styles/ButtonStyles'
import { breadcrumbsItemsActions } from '../../store/redux/reducers/breadcrumbsSlice'

const TechStackView = () => {
  // TRANSLATION
  const { t } = useTranslation()

  const theme = useTheme()
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'))
  const isLargeScreen = useMediaQuery(theme.breakpoints.down('lg'))

  // PAGE NAME
  const dispatch = useAppDispatch()
  useEffect(() => {
    dispatch(setPageName(t('page.name.techStack')))
    dispatch(pageHeaderActions.setInitialState())
    dispatch(breadcrumbsItemsActions.setInitialState())
    dispatch(
      breadcrumbsItemsActions.setSelectedItems({
        adminSettings: `${t('page.name.adminSettings.techStack')}`,
      })
    )
  }, [dispatch, t])

  // REQUEST PROPS
  const [requestProps, setRequestProps] = useState<RequestProps>({
    page: 0,
    size: 500,
  })

  const setRequestProp = (property: keyof RequestProps, input: string) => {
    setRequestProps({ ...requestProps, [property]: input })
  }

  // DENSITY
  const [dense, setDense] = useState<'dense' | 'full'>('full')

  // TECHNOLOGIES
  const { data: technologies, isLoading, isSuccess, isError } = useGetTechnologiesQuery(requestProps)

  // ADD TECHNOLOGY POPUP
  const [open, setOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null)

  const handleAddClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget)
    setOpen(true)
  }

  const handleDensitySelect = (event: React.MouseEvent<HTMLElement>, newDensity: 'dense' | 'full' | null) => {
    if (newDensity !== null) {
      setDense(newDensity)
    }
  }

  const { data: types } = useGetTechnologyTypesQuery({})

  return (
    <StyledContainerColumnLeft sx={{ gap: 4, p: 0 }}>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: theme.spacing(2),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: { xs: 'space-between', md: 'flex-start' },
            width: '100%',
            gap: { xs: theme.spacing(1), md: theme.spacing(2) },
          }}
        >
          {/* SEARCHBAR */}
          <Searchbar
            placeholder={t('components.placeholder.search')}
            handleChange={(value: string) => setRequestProp('name', value)}
          />

          {isMediumScreen ? (
            <ToggleButtonGroup exclusive value={dense} onChange={handleDensitySelect}>
              <Tooltip title={t('components.tooltip.denseGrid')}>
                <ToggleButton value={'dense'} aria-label={t('components.tooltip.denseGrid')}>
                  <ViewCompact />
                </ToggleButton>
              </Tooltip>
              <Tooltip title={t('components.tooltip.defaultGrid')}>
                <ToggleButton value={'full'} aria-label={t('components.tooltip.defaultGrid')}>
                  <ViewModule />
                </ToggleButton>
              </Tooltip>
            </ToggleButtonGroup>
          ) : null}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: { xs: 'space-between', md: 'flex-start' },
            width: { xs: '100%', md: 'fit-content' },
            gap: { xs: theme.spacing(1), md: theme.spacing(2) },
          }}
        >
          <Box sx={{ display: 'flex', gap: { xs: theme.spacing(0), sm: theme.spacing(1), md: theme.spacing(2) } }}>
            {/* TOGGLE SIZE */}
            {!isMediumScreen ? (
              <ToggleButtonGroup exclusive value={dense} size="small" onChange={handleDensitySelect}>
                <Tooltip title={t('components.tooltip.denseGrid')}>
                  <ToggleButton value={'dense'} aria-label={t('components.tooltip.denseGrid')}>
                    <ViewCompact />
                  </ToggleButton>
                </Tooltip>
                <Tooltip title={t('components.tooltip.defaultGrid')}>
                  <ToggleButton value={'full'} aria-label={t('components.tooltip.defaultGrid')}>
                    <ViewModule />
                  </ToggleButton>
                </Tooltip>
              </ToggleButtonGroup>
            ) : null}

            {/* SORT */}
            <Box
              sx={{
                marginX: { xs: 1, md: 2 },
              }}
            >
              <SortButton setRequestProps={setRequestProps} aria-label={t('components.button.sort')} />
            </Box>

            {/* FILTER */}
            <SingleFilterPopup
              singleFilterButtonText={t('components.button.filterTechnologyTypes')}
              singleFilterHeaderText={t('page.adminsPage.filtering.types')}
              popupId={'filter-technology'}
              setRequestProps={setRequestProps}
              dynamicPropName={'type'}
              optionsToChoose={types?.map(value => ({
                value: value,
                label: formatTechnologyType(value),
              }))}
              notFoundText={t('components.autocomplete.technologies.technologiesNotFound')}
              aria-label={t('components.button.filterTechnologyTypes')}
            />
          </Box>

          {/* ADD */}
          <Box onClick={handleAddClick}>
            {isLargeScreen ? (
              <Tooltip
                title={
                  <Stack alignItems={'center'}>
                    <Typography sx={{ px: 1 }}>{t('components.button.addTechnology')}</Typography>
                  </Stack>
                }
              >
                <SquareButton
                  color="primary"
                  size="medium"
                  sx={{ height: 34 }}
                  aria-label={t('components.button.addTechnology')}
                >
                  <Add />
                </SquareButton>
              </Tooltip>
            ) : (
              <Button
                variant="contained"
                color="primary"
                startIcon={<Add />}
                size="medium"
                sx={{ height: 34 }}
                aria-label={t('components.button.addTechnology')}
              >
                {t('components.button.addTechnology')}
              </Button>
            )}
          </Box>
          <AddTechnologyPopup open={open} setOpen={setOpen} anchorEl={anchorEl} />
        </Box>
      </Box>

      {/* TECH TILES */}
      <TechTilesContainer dense={dense === 'dense'}>
        {isLoading && (
          <StyledContainerCentered>
            <CircularProgress />
          </StyledContainerCentered>
        )}

        {isError && (
          <StyledContainerCentered>
            <Message>{t('page.adminsPage.messages.getTechnologiesError')}</Message>
          </StyledContainerCentered>
        )}

        {isSuccess && technologies?.content.length !== 0
          ? technologies.content.map(technology => (
              <TechnologyTile
                isCollapsed={dense === 'dense'}
                technology={technology}
                key={`technology-tile-${technology.id}`}
              />
            ))
          : !isLoading && <Message>{t('page.adminsPage.messages.technologiesNotFound')}</Message>}
      </TechTilesContainer>
    </StyledContainerColumnLeft>
  )
}

export default TechStackView
